import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications, getUnreadCount, markRead, markAllRead, deleteNotification,
  type AppNotification,
} from '@/lib/api/notifications';

const typeIcon: Record<string, string> = {
  invoice_overdue:  'receipt_long',
  invoice_paid:     'paid',
  invoice_sent:     'send',
  job_in_progress:  'construction',
  job_started:      'construction',
  job_completed:    'task_alt',
  job_on_hold:      'pause_circle',
  job_cancelled:    'cancel',
  quote_approved:   'thumb_up',
  quote_rejected:   'thumb_down',
  change_order:     'edit_note',
  time_submitted:   'schedule',
  task_overdue:     'assignment_late',
  work_lead:        'construction',
  warning:          'warning',
  default:          'notifications',
};

export const NotificationsPanel = ({ dark = false }: { dark?: boolean } = {}) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [notes, count] = await Promise.all([getNotifications(), getUnreadCount()]);
      setNotifications(notes);
      setUnread(count);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = async (n: AppNotification) => {
    if (!n.is_read) await markRead(n.id).catch(() => {});
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
    setUnread(prev => Math.max(0, prev - (n.is_read ? 0 : 1)));
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  const handleMarkAll = async () => {
    await markAllRead().catch(() => {});
    setNotifications(prev => prev.map(x => ({ ...x, is_read: true })));
    setUnread(0);
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    await deleteNotification(id).catch(() => {});
    setNotifications(prev => prev.filter(x => x.id !== id));
    setUnread(prev => Math.max(0, prev));
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`relative p-2 rounded-lg ${
          dark
            ? 'text-gray-400 hover:text-gray-200 hover:bg-white/10'
            : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
        }`}
        title="Notifications"
      >
        <span className="material-symbols-outlined text-xl">notifications</span>
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-gray-900 text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={handleMarkAll} className="text-xs text-[#C77D3F] hover:underline">
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">All caught up!</div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${!n.is_read ? 'bg-amber-50/60' : ''}`}
                >
                  <span className={`material-symbols-outlined text-lg mt-0.5 flex-shrink-0 ${!n.is_read ? 'text-[#C77D3F]' : 'text-gray-400'}`}>
                    {typeIcon[n.type] ?? typeIcon.default}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {n.title}
                    </p>
                    {n.body && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[11px] text-gray-400 mt-1">
                      {new Date(n.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <button
                    onClick={e => handleDelete(e, n.id)}
                    className="text-gray-300 hover:text-gray-500 flex-shrink-0 mt-0.5"
                  >
                    <span className="material-symbols-outlined text-base">close</span>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
